import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { SaveBar } from "./SettingsLayout";
import { LANGUAGES, DATE_FORMATS, TIME_FORMATS, WEEK_STARTS } from "./lib";
import { useAuth } from "@/context/AuthContext";

export default function Preferences() {
  const { user } = useAuth();
  const branchOnly = user?.role === "branch_manager";   // may only change working_hours
  const readOnly = !["owner", "branch_manager"].includes(user?.role);

  const [initial, setInitial] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/settings/preferences");
      setInitial(r.data); setForm(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load preferences"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const dirty = form && JSON.stringify(form) !== JSON.stringify(initial);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch("/settings/preferences", form);
      setInitial(r.data); setForm(r.data);
      toast.success("Preferences saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  if (loading || !form) return <div className="p-8 text-center text-[13px] text-[#667085]">Loading…</div>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setWH = (k, v) => setForm((f) => ({ ...f, working_hours: { ...f.working_hours, [k]: v } }));

  const disabledBusiness = readOnly || branchOnly;   // most fields locked for branch manager

  return (
    <div className="space-y-4" data-testid="settings-preferences">
      <Card title="Localisation" testId="prefs-loc">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <F label="Default language">
            <select value={form.language} onChange={(e) => set("language", e.target.value)}
                    disabled={disabledBusiness} data-testid="prefs-language"
                    className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full bg-white">
              {LANGUAGES.map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
            </select>
          </F>
          <F label="Date format">
            <select value={form.date_format} onChange={(e) => set("date_format", e.target.value)}
                    disabled={disabledBusiness} data-testid="prefs-date-format"
                    className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full bg-white">
              {DATE_FORMATS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </F>
          <F label="Time format">
            <select value={form.time_format} onChange={(e) => set("time_format", e.target.value)}
                    disabled={disabledBusiness} data-testid="prefs-time-format"
                    className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full bg-white">
              {TIME_FORMATS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </F>
          <F label="Week starts on">
            <select value={form.week_starts_on} onChange={(e) => set("week_starts_on", e.target.value)}
                    disabled={disabledBusiness} data-testid="prefs-week-start"
                    className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full bg-white">
              {WEEK_STARTS.map((w) => <option key={w.key} value={w.key}>{w.label}</option>)}
            </select>
          </F>
        </div>
      </Card>

      <Card title="Working hours & appointments" testId="prefs-hours">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <F label="Opens at">
            <input type="time" value={form.working_hours?.open || "09:00"}
                   onChange={(e) => setWH("open", e.target.value)}
                   disabled={readOnly} data-testid="prefs-open"
                   className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full bg-white font-mono" />
          </F>
          <F label="Closes at">
            <input type="time" value={form.working_hours?.close || "20:00"}
                   onChange={(e) => setWH("close", e.target.value)}
                   disabled={readOnly} data-testid="prefs-close"
                   className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full bg-white font-mono" />
          </F>
          <F label="Default appointment duration (min)">
            <input type="number" min={15} step={15} value={form.default_appointment_duration}
                   onChange={(e) => set("default_appointment_duration", Number(e.target.value) || 60)}
                   disabled={disabledBusiness} data-testid="prefs-appt-duration"
                   className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full bg-white font-mono" />
          </F>
        </div>
      </Card>

      <Card title="Workshop rules" testId="prefs-rules">
        <div className="space-y-2.5">
          <ToggleRow
            label="Allow negative stock"
            desc="Let mechanics issue parts even when the tracked quantity would go below zero."
            checked={form.allow_negative_stock}
            onChange={(v) => set("allow_negative_stock", v)}
            testId="prefs-neg-stock"
            disabled={disabledBusiness}
          />
          <ToggleRow
            label="Require full payment before Gate Pass"
            desc="Block gate pass issuance until the linked invoice is fully paid. Owners may still override with an audited reason."
            checked={form.require_full_payment_before_gate_pass}
            onChange={(v) => set("require_full_payment_before_gate_pass", v)}
            testId="prefs-full-pay-gp"
            disabled={disabledBusiness}
          />
          <ToggleRow
            label="Quality check required before delivery"
            desc="A completed quality check must be recorded before the job card moves to Ready for Delivery."
            checked={form.quality_check_required}
            onChange={(v) => set("quality_check_required", v)}
            testId="prefs-qc-required"
            disabled={disabledBusiness}
          />
          <ToggleRow
            label="Customer signature required on Job Card"
            desc="Prompt for a customer signature at check-in."
            checked={form.customer_signature_required_on_job_card}
            onChange={(v) => set("customer_signature_required_on_job_card", v)}
            testId="prefs-cust-sign"
            disabled={disabledBusiness}
          />
        </div>
      </Card>

      {!readOnly && <SaveBar dirty={!!dirty} saving={saving} onSave={save} />}
      {branchOnly && (
        <div className="rounded-md bg-[#F1FAEE] border border-[#a8dadc]/40 px-3 py-2 text-[12px] text-[#457b9d]">
          Branch managers can only change working hours. Other preferences are locked to the Garage Owner.
        </div>
      )}
      {readOnly && (
        <div className="rounded-md bg-[#F1FAEE] border border-[#a8dadc]/40 px-3 py-2 text-[12px] text-[#457b9d]">
          Only the Garage Owner or Branch Manager can view preferences.
        </div>
      )}
    </div>
  );
}

function Card({ title, children, testId }) {
  return (
    <section className="bg-white rounded-xl border border-[#E4E7EC]" data-testid={testId}>
      <header className="px-4 py-3 border-b border-[#F1FAEE]">
        <h3 className="text-[13px] font-bold text-[#1d3557]">{title}</h3>
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}
function F({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function ToggleRow({ label, desc, checked, onChange, testId, disabled }) {
  return (
    <div className={`flex items-start gap-3 rounded-lg border border-[#E4E7EC] px-3 py-2.5 ${disabled ? "opacity-70" : ""}`}>
      <input type="checkbox" checked={checked}
             onChange={(e) => onChange(e.target.checked)}
             disabled={disabled} data-testid={testId}
             className="w-4 h-4 accent-[#e63946] mt-1" />
      <div>
        <div className="text-[13px] font-semibold text-[#1d3557]">{label}</div>
        <div className="text-[11.5px] text-[#667085]">{desc}</div>
      </div>
    </div>
  );
}
